import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { GuidePage } from "@/components/GuidePage";
import { guideArt } from "@/data/guideArt";
import type { StackGuide } from "@/data/stackGuides";

const codeBlock = (value: string) => (
  <pre dir="ltr" className="overflow-x-auto rounded-tile bg-slate-950 p-5 text-left text-sm leading-7 text-slate-200">
    <code>{value}</code>
  </pre>
);

function getCustomAnswer(guide: StackGuide): ReactNode {
  switch (guide.slug) {
    case "nodejs":
      return (
        <p>
          برای {guide.primaryKeyword} و فریم‌ورک‌های Express و NestJS، مخزن پروژه را به سکوی ابری متصل کنید یا از دستور <code>gerdoo deploy</code> استفاده نمایید. گردو وابستگی‌های پروژه را نصب کرده، برنامه را در کانتینر ایزوله اجرا می‌کند و دامنه اختصاصی با SSL خودکار تحویل می‌دهد. برای میزبانی مطمئن به بخش <Link to="/products/nodejs">هاست ابری nodejs</Link> یا صفحه <Link to="/guides">آموزش دیپلوی</Link> سر بزنید.
        </p>
      );
    case "laravel":
      return (
        <p>
          فرآیند {guide.primaryKeyword} و اجرای دستورات Artisan روی سکوی ابری گردو بدون نیاز به کنترل‌پنل‌های سنتی انجام می‌شود. با اتصال مستقیم به گیت، وابستگی‌های کامپوزر نصب شده و امکان اتصال سریع به <Link to="/marketplace/apps/mysql">خرید دیتابیس mysql</Link> فراهم می‌گردد. همچنین برای مشاهده پلن‌ها می‌توانید صفحه <Link to="/products/laravel">خرید هاست لاراول</Link> یا راهنمای جامع <Link to="/guides">آموزش دیپلوی</Link> را بررسی کنید.
        </p>
      );
    case "go":
      return (
        <p>
          برای {guide.primaryKeyword} و فریم‌ورک‌های Gin، Echo و Fiber، کافی است باینری پروژه را مستقیماً از گیت کامپایل و اجرا کنید. برای اطلاعات بیشتر درباره زیرساخت زبان گو، صفحه <Link to="/products/go">هاست golang</Link> و مستندات <Link to="/guides">آموزش دیپلوی</Link> را مطالعه نمایید.
        </p>
      );
    case "ruby":
      return (
        <p>
          برای {guide.primaryKeyword} و استقرار برنامه‌های Rails و Sinatra، مخزن کد را متصل کنید تا Bundler به صورت خودکار gemها را نصب کند. برای بررسی امکانات سرور روبی، صفحه <Link to="/products/ruby">هاست روبی</Link> و راهنمای <Link to="/guides">آموزش دیپلوی</Link> در دسترس شماست.
        </p>
      );
    case "rust":
      return (
        <p>
          برای {guide.primaryKeyword} و فریم‌ورک‌های Axum و Actix Web، کامپایل بهینه‌سازی‌شده نسخه release مستقیماً روی سرور انجام می‌شود. جهت بهره‌مندی از محیط‌های کانتینری به صفحه <Link to="/products/containers">کانتینر ابری</Link>، بخش اختصاصی <Link to="/products/rust">هاست rust</Link> و راهنمای <Link to="/guides">آموزش دیپلوی</Link> مراجعه فرمایید.
        </p>
      );
    case "react":
      return (
        <p>
          برای {guide.primaryKeyword} و پروژه‌های ساخته‌شده با Vite، بیلد خودکار کدهای جاوااسکریپت روی بستر ابری اجرا شده و صفحات در دیسک‌های پرسرعت کش می‌شوند. برای مقایسه با فریم‌ورک‌های دیگر می‌توانید صفحه <Link to="/products/react">هاست react</Link>، راهنمای <Link to="/guides/deploy-nextjs">دیپلوی nextjs</Link> و صفحه <Link to="/guides">آموزش دیپلوی</Link> را ببینید.
        </p>
      );
    case "nextjs":
      return (
        <p>
          در {guide.primaryKeyword} با پشتیبانی از Server-Side Rendering (SSR) و App Router، وب‌سایت‌های مدرن فرانت‌اند با حداکثر سرعت در دیتاسنترهای داخلی لود می‌شوند. برای اطلاعات بیشتر از صفحه تخصصی <Link to="/products/nextjs">هاست Next js</Link>، راهنمای <Link to="/guides/deploy-react">دیپلوی react</Link> و راهنمای کلی <Link to="/guides">آموزش دیپلوی</Link> بازدید کنید.
        </p>
      );
    case "vue":
      return (
        <p>
          جهت {guide.primaryKeyword} و استقرار اپلیکیشن‌های تک‌صفحه‌ای یا فریم‌ورک Nuxt، فرآیند بیلد کانتینری و اتصال دامنه تنها در چند دقیقه انجام می‌پذیرد. برای مشاهده گزینه‌های میزبانی به صفحه <Link to="/products/vue">هاست vue</Link> و بخش جامع <Link to="/guides">آموزش دیپلوی</Link> مراجعه نمایید.
        </p>
      );
    case "svelte":
      return (
        <p>
          برای {guide.primaryKeyword} و استفاده از سرعت کم‌نظیر SvelteKit، مخزن کد را متصل کرده و از بیلد خودکار ابری بهره ببرید. برای اطلاعات بیشتر، بخش <Link to="/products/svelte">هاست svelte</Link> و راهنماهای <Link to="/guides">آموزش دیپلوی</Link> پیش روی شماست.
        </p>
      );
    case "astro":
      return (
        <p>
          برای {guide.primaryKeyword} چه در حالت تولید سایت ایستا و چه در حالت سرورساید رندرینگ (SSR)، صفحات وب با کمترین تاخیر در شبکه داخلی منتشر می‌شوند. برای میزبانی تخصصی به صفحه <Link to="/products/astro">هاست astro</Link> و صفحه <Link to="/guides">آموزش دیپلوی</Link> مراجعه کنید.
        </p>
      );
    case "static-sites":
      return (
        <p>
          در {guide.primaryKeyword} فایل‌های آماده HTML، CSS و JS بدون نیاز به کانفیگ وب‌سرورهای پیچیده منتشر می‌شوند. شما می‌توانید از خدمات اختصاصی <Link to="/products/static-sites">هاست استاتیک</Link>، پلن رایگان در <Link to="/free-host">هاست رایگان پرسرعت</Link> و مستندات <Link to="/guides">آموزش دیپلوی</Link> استفاده نمایید.
        </p>
      );
    default:
      return (
        <p>
          برای استقرار پروژه روی سکوی ابری گردو، مخزن گیت خود را متصل کنید یا از دستور <code>gerdoo deploy</code> استفاده نمایید.
        </p>
      );
  }
}

function getCustomFaq(guide: StackGuide): Array<{ q: string; a: string }> {
  switch (guide.slug) {
    case "nodejs":
      return [
        {
          q: "آموزش دیپلوی nodejs در گردو کلاد چگونه است؟",
          a: "در آموزش دیپلوی nodejs کافی است پروژه Express یا NestJS خود را به گیت متصل کنید. پلتفرم با شناسایی package.json به صورت خودکار npm install و build را اجرا کرده و با دستور npm start برنامه را آنلاین می‌کند.",
        },
        {
          q: "چگونه دیپلوی پروژه node js را با متغیرهای محیطی امن انجام دهیم؟",
          a: "در دیپلوی پروژه node js مقادیر محرمانه نظیر کلیدهای API و رشته اتصال دیتابیس را در بخش متغیرهای محیطی داشبورد وارد کنید تا به شکل امن در process.env در دسترس برنامه قرار گیرند.",
        },
        {
          q: "آیا دیپلوی express و دیپلوی nestjs از دیتابیس‌های ابری پشتیبانی می‌کنند؟",
          a: "بله، در دیپلوی express و دیپلوی nestjs می‌توانید دیتابیس‌های MongoDB، PostgreSQL یا Redis را در چند ثانیه اضافه کرده و بدون تنظیمات دستی به سرویس متصل کنید.",
        },
      ];
    case "laravel":
      return [
        {
          q: "آموزش دیپلوی لاراول و اجرای مایگریشن‌ها در گردو چگونه است؟",
          a: "در آموزش دیپلوی لاراول می‌توانید در فیلد دستور ساخت عبارت php artisan migrate --force را قرار دهید تا دیتابیس شما همزمان با انتشار نسخه جدید به‌روزرسانی شود.",
        },
        {
          q: "آیا آپلود پروژه لاراول روی هاست بدون سی‌پنل ممکن است؟",
          a: "بله، فرآیند آپلود پروژه لاراول روی هاست ابری گردو نیازی به کنترل‌پنل‌های سنتی ندارد و تمام فرآیند استقرار مستقیم از طریق گیت یا CLI انجام می‌پذیرد.",
        },
        {
          q: "دیپلوی پروژه لاراول و دیپلوی php برای صف‌ها (Queue) چگونه مدیریت می‌شود؟",
          a: "در دیپلوی پروژه لاراول و به طور کلی دیپلوی php می‌توانید یک سرویس پردازشگر مجزا (Worker) با دستور php artisan queue:work ایجاد کنید تا وظایف پس‌زمینه به شکل پایدار اجرا شوند.",
        },
      ];
    case "go":
      return [
        {
          q: "آموزش دیپلوی golang برای فریم‌ورک‌های Gin و Fiber چگونه است؟",
          a: "در آموزش دیپلوی golang کافی است فایل go.mod در مخزن قرار داشته باشد. گردو دستور go build را اجرا کرده و باینری کامپایل‌شده سبک را در کانتینر ایزوله به اجرا درمی‌آورد.",
        },
        {
          q: "دیپلوی پروژه go و دیپلوی گو بدون داکرفایل پشتیبانی می‌شود؟",
          a: "بله، در دیپلوی پروژه go و دیپلوی گو پلتفرم به صورت هوشمند ساختار زبان را شناسایی می‌کند، هرچند امکان تعریف داکرفایل اختصاصی چندمرحله‌ای (Multi-stage) نیز فراهم است.",
        },
      ];
    case "ruby":
      return [
        {
          q: "دیپلوی ruby و استقرار دیپلوی rails به چه شکل است؟",
          a: "در دیپلوی ruby برای فریم‌ورک محبوب روبی آن ریلز، دستور bundle install و کامپایل استاتیک اسست‌ها به صورت خودکار اجرا شده و وب‌سرور Puma پروژه را آنلاین می‌کند.",
        },
        {
          q: "دیپلوی ruby on rails و دیپلوی روبی با دیتابیس چگونه کانفیگ می‌شود؟",
          a: "در دیپلوی ruby on rails و دیپلوی روبی، اطلاعات اتصال به دیتابیس از طریق متغیر محیطی DATABASE_URL به برنامه تزریق می‌شود و نیازی به هاردکد کردن اطلاعات حساس در کدهای پروژه نیست.",
        },
      ];
    case "rust":
      return [
        {
          q: "آموزش دیپلوی rust و نحوه کامپایل در سرور چگونه است؟",
          a: "در آموزش دیپلوی rust دستور cargo build --release به صورت اتوماتیک اجرا می‌شود تا باینری نهایی با بالاترین سرعت و کمترین مصرف منابع سخت‌افزاری اجرا گردد.",
        },
        {
          q: "دیپلوی پروژه rust چه میزان رم نیاز دارد؟",
          a: "در دیپلوی پروژه rust به دلیل مصرف حافظه در مرحله کامپایل، منابع مناسبی در زمان بیلد تخصیص می‌یابد اما پس از اجرا، باینری بهینه‌سازی‌شده کمترین مصرف رم را خواهد داشت.",
        },
      ];
    case "react":
      return [
        {
          q: "دیپلوی react و دیپلوی ری اکت روی هاست ابری چگونه انجام می‌شود؟",
          a: "در دیپلوی react و دیپلوی ری اکت، کدهای پروژه با ابزارهایی مانند Vite یا Webpack بیلد شده و خروجی پوشه dist با وب‌سرور پرسرعت و کش مناسب در بستر ابری منتشر می‌شود.",
        },
        {
          q: "آپلود پروژه react روی هاست و دیپلوی پروژه react برای روت‌های SPA چگونه حل می‌شود؟",
          a: "در آپلود پروژه react روی هاست و دیپلوی پروژه react، تنظیمات بازنویسی آدرس‌ها (Fallback Routing) فعال است تا در رفرش صفحات مرورگر با خطای ۴۰۴ مواجه نشوید.",
        },
      ];
    case "nextjs":
      return [
        {
          q: "دیپلوی nextjs و دیپلوی next js با حالت SSR چگونه است؟",
          a: "در دیپلوی nextjs و دیپلوی next js دستور npm run build اجرا شده و سرور Node.js برنامه را برای رندر سمت سرور و روت‌های API فعال نگه می‌دارد.",
        },
        {
          q: "آپلود پروژه next js روی هاست و دیپلوی نکست چه تفاوتی با استاتیک دارد؟",
          a: "در آپلود پروژه next js روی هاست و دیپلوی نکست نیازی به خروجی ایستا نیست و تمامی قابلیت‌های مدرن App Router و کش هوشمند به طور کامل پشتیبانی می‌شوند.",
        },
      ];
    case "vue":
      return [
        {
          q: "دیپلوی vue و دیپلوی nuxt در گردو کلاد چگونه انجام می‌شود؟",
          a: "برای دیپلوی vue و پروژه‌های Nuxt SSR، سیستم به صورت خودکار فایل‌های پکیج را خوانده و نسخه سروری یا تک‌صفحه‌ای را با بالاترین کیفیت دیپلوی می‌کند.",
        },
        {
          q: "آپلود پروژه vue روی هاست برای صفحات فرانت‌اند چه مزیتی دارد؟",
          a: "در آپلود پروژه vue روی هاست، دارایی‌های جاوااسکریپت روی شبکه دیسک‌های پرسرعت قرار گرفته و زمان باز شدن صفحات به حداقل می‌رسد.",
        },
      ];
    case "svelte":
      return [
        {
          q: "دیپلوی svelte و دیپلوی sveltekit چگونه پیکربندی می‌شود؟",
          a: "در دیپلوی svelte و دیپلوی sveltekit کافی است از آداپتور سازگار نظیر adapter-node استفاده کنید تا پلتفرم برنامه را در چند ثانیه منتشر نماید.",
        },
      ];
    case "astro":
      return [
        {
          q: "دیپلوی astro و دیپلوی پروژه astro برای سایت‌های محتوا‌محور چگونه است؟",
          a: "در دیپلوی astro و دیپلوی پروژه astro، صفحات استاتیک با کمترین حجم جاوااسکریپت بیلد شده و تحویل داده می‌شوند.",
        },
      ];
    case "static-sites":
      return [
        {
          q: "آپلود سایت html و انتشار سایت html در هاست استاتیک گردو چگونه است؟",
          a: "در آپلود سایت html و انتشار سایت html کافی است فایل‌های HTML و CSS را متصل کنید تا بلافاصله با گواهی SSL رایگان در دسترس قرار گیرند.",
        },
        {
          q: "آپلود سایت html روی هاست چه تاثیری بر سرعت بارگذاری دارد؟",
          a: "در آپلود سایت html روی هاست به دلیل حذف پردازش‌های سنگین دیتابیس، صفحات وب با بالاترین سرعت ممکن بارگذاری می‌شوند.",
        },
      ];
    default:
      return [];
  }
}

export function StackDeploymentGuide({ guide }: { guide: StackGuide }) {
  const faq = getCustomFaq(guide);
  const command = `# دستور ساخت\n${guide.buildCommand}\n\n# دستور اجرا\n${guide.runCommand}`;

  return (
    <GuidePage
      title={guide.h1Title}
      seoTitle={guide.seoTitle}
      h1Title={guide.h1Title}
      description={guide.seoDescription}
      keywords={guide.keywords}
      badge={`راهنمای استقرار ${guide.name}`}
      canonicalPath={`/guides/deploy-${guide.slug}`}
      art={guideArt(guide.slug)}
      updated="۱۴۰۵/۰۶/۱۵"
      updatedIso="2026-09-06"
      answer={getCustomAnswer(guide)}
      sections={[
        {
          title: `آماده‌سازی پروژه برای ${guide.primaryKeyword}`,
          body: (
            <>
              <p>
                فایل‌های وابستگی پروژه یعنی <code>{guide.dependencyFile}</code> را در مخزن گیت قرار دهید. مقادیر پیش‌فرض دستور ساخت و دستور اجرا برای این استک:
              </p>
              {codeBlock(command)}
              <p>{guide.portNote}</p>
            </>
          ),
        },
        {
          title: "استقرار خودکار مستقیم از مخزن Git",
          body: (
            <ol>
              <li>در پنل کاربری گردو کلاد یک پروژه و محیط جدید ایجاد کنید.</li>
              <li>سرویس را به مخزن GitHub یا GitLab و شاخه مورد نظر متصل نمایید.</li>
              <li>دستور ساخت و دستور اجرا را مطابق با نیازمندی‌های پروژه تنظیم کنید.</li>
              <li>متغیرهای محیطی ضروری را تعریف کرده و فرآیند استقرار را آغاز نمایید.</li>
              <li>در بخش دامنه‌ها، دامنه‌ی اختصاصی خود را متصل کرده و از SSL رایگان بهره‌مند شوید.</li>
            </ol>
          ),
        },
        {
          title: "استقرار از پوشه محلی با Gerdoo CLI",
          body: (
            <>
              {codeBlock(
                "curl -fsSL https://get.gerdoo.cloud/install.sh | sh\ngerdoo login\ncd your-project\ngerdoo deploy\ngerdoo logs"
              )}
              <p>
                ابزار خط فرمان گردو امکان انتشار مستقیم کدهای محلی را بدون نیاز به ارسال به گیت فراهم می‌سازد.
              </p>
              <p>
                در Windows (PowerShell) به‌جای دستور نصب از <code dir="ltr">irm https://get.gerdoo.cloud/install.ps1 | iex</code> استفاده کنید.
              </p>
            </>
          ),
        },
        {
          title: `نکات مهم محیط عملیاتی برای ${guide.name}`,
          body: (
            <ul>
              {guide.productionNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
              <li>بررسی سلامت و زمان شروع به کار سرویس را پیش از هدایت ترافیک واقعی تست کنید.</li>
              <li>داده‌های دائمی و فایل‌های کاربران را روی دیسک‌های ماندگار یا ذخیره‌سازی ابری نگه دارید.</li>
            </ul>
          ),
        },
      ]}
      faq={faq}
      related={guide.relatedLinks}
    />
  );
}
